/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Container,
  Grid,
  Typography,
  CssBaseline,
  makeStyles,
  IconButton,
  Chip,
  Box,
  useTheme,
  OutlinedInput,
  InputAdornment,
  Button,
} from '@material-ui/core';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@material-ui/icons';

import {
  ApplicationState,
  UserReducer,
  StorageOperations,
  ApiReducer,
  Api,
  ApiTag,
  ApiTagsReducer,
  ApiList,
  ApiCategory,
  ApiCategoriesReducer,
  OrganizationMetadataReducer,
} from '../data/interfaces';
import {
  fetchApiListAuthenticated,
  fetchApiListUnAuthenticated,
  fetchApiTagList,
  fetchApiCategoryList,
  fetchApiListByMajorVersionAuthenticated,
  fetchApiListByMajorVersionUnAuthenticated,
} from '../data/operations';
import {
  RETURN_TO_PATH,
  SELECTED_ORG_UUID,
  ORG_QUERY_PARAM,
  FIDP,
  RESOURCE_SIGN_IN,
  RESOURCE_LOGIN,
  API_TAGS_QUERY,
  API_CATEGORY_QUERY,
  RGB_ALPHA,
  DEFAULT_RGB_ALPHA,
  DEFAULT_API_TAGS_COLOR,
  API_GENERAL_QUERY,
  KEY_ENTER,
  SELECTED_ORG_HANDLE_NAME, API_DISPLAY_NAME_QUERY,
} from '../data/constants';
import {
  isValidData,
  sessionStoreOperation,
  localStoreOperation,
  getIsInternalAPI,
  isSelfSignupEnabled,
  getAPIType,
} from '../data/utils';
import DevPortalFooter from './shared/DevPortalFooter';
import ApiCard from './api-list/ApiCard';
import NoPermission from './shared/NoPermission';
import {trackApiLoadOnSearch} from "../util/app-insights";
import Settings from '../conf/settings';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexGrow: 1,
    height: '100vh',
    flexDirection: 'column',
  },
  appBarSpacer: {
    minHeight: theme.spacing(16),
  },
  content: {
    flexGrow: 1,
    overflow: 'auto',
    ...theme.custom.apiListingPage.styles,
  },
  container: {
    paddingTop: theme.spacing(5.75),
    paddingBottom: theme.spacing(4),
    maxWidth: theme.spacing(185),
  },
  drawerPaper: {
    position: 'relative',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    borderRight: 'none',
    backgroundColor: '#f7f8fb',
  },
  listPadding: {
    display: 'flex',
    paddingTop: '0',
    paddingBottom: '0',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row',
    },
  },
  apiListPageTitle: {
    ...theme.custom.apiListingPage.title.styles,
  },
  apiListPageSubTitle: {
    ...theme.custom.apiListingPage.subTitle.styles,
  },
  apiListGrid: {
    minWidth: theme.spacing(38.5),
    minHeight: theme.spacing(30.5),
  },
  allApisButton: {
    height: theme.spacing(4),
    width: theme.spacing(9),
    margin: '17px 8px 8px 8px',
    fontSize: '11px',
    textTransform: 'capitalize',
    backgroundColor: 'rgba(255,255,255,0)',
    borderColor: '#f7f8fb',
    borderRadius: '7px',
    color: '#40404B',
    fontFamily: 'GilmerMedium',
    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
  },
  allApisButtonBox: {
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  apiTags: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
    flexWrap: 'wrap',
    listStyle: 'none',
    padding: theme.spacing(0.5),
    margin: 0,
  },
  apiCategories: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      flexDirection: 'row',
    },
    flexWrap: 'wrap',
    listStyle: 'none',
    padding: theme.spacing(0.5),
    margin: 0,
  },
  apiCategoriesBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
    overflow: 'auto',
  },
  apiTagCategorySection: {
    paddingLeft: theme.spacing(),
    [theme.breakpoints.down('md')]: {
      paddingLeft: '0px',
    },
  },
  apiTagCategoryTitle: {
    ...theme.custom.apiListingPage.tagCategory.styles,
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  categoryChip: {
    margin: theme.spacing(0.5),
    backgroundColor: '#f7f8fb',
    border: 'none',
    borderRadius: '6px',
  },
  tagChip: {
    margin: theme.spacing(0.5),
    backgroundColor: '#a6b3ff',
    border: 'none',
    borderRadius: '6px',
    ...theme.custom.apiListingPage.tagChip
  },
  apiSearchBar: {
    [theme.breakpoints.down('sm')]: {
      width: theme.spacing(50),
    },
    ...theme.custom.apiListingPage.apiSearchBar.textField,
  },
  apiSearchBarButton: {
    ...theme.custom.apiListingPage.apiSearchBar.searchButton,
  },
  apiSearchBarAdornedEnd: {
    paddingRight: theme.spacing(1),
  },
  apiSearchBarAdornedStart: {
    paddingLeft: theme.spacing(1),
  },
  apiSearchBarInputAdornedStart: {
    paddingLeft: theme.spacing(1),
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    height: theme.spacing(4.5),
  },
}));

export interface ApiListProps {
  apiReducer: ApiReducer;
  userReducer: UserReducer;
  apiTagsReducer: ApiTagsReducer;
  apiCategoriesReducer: ApiCategoriesReducer;
  organizationMetadataReducer: OrganizationMetadataReducer;
  fetchApiListAuthenticated: (
    organizationId: string,
    pagination?: string
  ) => Promise<void>;
  fetchApiListByMajorVersionAuthenticated: (
    organizationId: string,
    pagination?: string
  ) => Promise<void>;
  fetchApiListUnAuthenticated: (
      organizationId: string,
      pagination?: string
  ) => Promise<void>;
  fetchApiListByMajorVersionUnAuthenticated: (
    organizationId: string,
    pagination?: string
) => Promise<void>;
  fetchApiTagList: (organizationId: string) => Promise<void>;
  fetchApiCategoryList: (organizationId: string) => Promise<void>;
}

function ApiListPage({
  apiReducer,
  userReducer,
  apiTagsReducer,
  apiCategoriesReducer,
  organizationMetadataReducer,
  fetchApiListAuthenticated,
  fetchApiListUnAuthenticated,
  fetchApiListByMajorVersionAuthenticated,
  fetchApiListByMajorVersionUnAuthenticated,
  fetchApiTagList,
  fetchApiCategoryList,
}: ApiListProps) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const intl = useIntl();
  const { orgName } = useParams<{orgName: string}>();
  const { loading: apiLoading, apiList, thumbnailMap } = apiReducer;
  const publicOrgUuid = organizationMetadataReducer?.organizationMetadata?.uuid;
  const {
    apimTokenObtained,
    orgs: userOrganizations,
    selectedOrg,
    signedIn
  } = userReducer;
  const { tagList } = apiTagsReducer;
  const { categoryList } = apiCategoriesReducer;
  const [tagCounts, setTagCounts] = useState({ minValue: 0, maxValue: 0 });
  const [apiSearchBarText, setApiSearchBarText] = useState('');
  const [apiListState, setApiListState] = useState<{
    apiList: ApiList;
    thumbnailMap: { [key: string]: string };
    tagList: ApiTag[];
    categoryList: ApiCategory[];
  }>({
    apiList: apiList,
    thumbnailMap: thumbnailMap,
    tagList: tagList.list,
    categoryList: categoryList.list,
  });

  const checkUserAccessToCurrentPathOrg = (): boolean => {
    const selectedOrganization = userOrganizations.find(
      (userOrg) => userOrg.handle === orgName
    );
    return !!selectedOrganization;
  };

  const hasUserAccessToCurrentPathOrg = checkUserAccessToCurrentPathOrg();

  const getApiListAuthenticated = async (organizationId: string, pagination?: string) => {
    if (Settings.intelligentRoutingFeatureEnabled) {
      fetchApiListByMajorVersionAuthenticated(organizationId, pagination);
    } else {
      fetchApiListAuthenticated(organizationId, pagination);
    }
  }

  const getApiListUnAuthenticated = async (organizationId: string, pagination?: string) => {
    if (Settings.intelligentRoutingFeatureEnabled) {
      fetchApiListByMajorVersionUnAuthenticated(organizationId, pagination);
    } else {
      fetchApiListUnAuthenticated(organizationId, pagination);
    }
  }

  const getApiNextPage = () => {
    const paginationNext = apiListState.apiList?.pagination?.next;
    if (paginationNext && selectedOrg.uuid) {
      getApiListAuthenticated(selectedOrg.uuid, paginationNext);
    } else if (paginationNext && publicOrgUuid && !apimTokenObtained) {
      getApiListUnAuthenticated(publicOrgUuid, paginationNext);
    }
    document
      .querySelector('#apiListMainContent')
      ?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getApiPreviousPage = () => {
    const paginationPrev = apiListState.apiList?.pagination?.previous;
    if (paginationPrev && selectedOrg.uuid && apimTokenObtained) {
      getApiListAuthenticated(selectedOrg.uuid, paginationPrev);
    } else if (paginationPrev && publicOrgUuid && !apimTokenObtained) {
      getApiListUnAuthenticated(publicOrgUuid, paginationPrev);
    }
    document
      .querySelector('#apiListMainContent')
      ?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTagClick = (tagValue?: string) => {
    if (!tagValue && selectedOrg.uuid) {
      getApiListAuthenticated(selectedOrg.uuid);
    } else if (tagValue && selectedOrg.uuid) {
      // Uri encode the value to preserve the whitespaces in the tag. i.e tags:Business Intelligence/Dashboards
      // Wrap tag value with double quotes to let the API Manager know that the tag value should be matched exactly
      const selectedTag = encodeURIComponent("\"" + tagValue + "\"");
      getApiListAuthenticated(selectedOrg.uuid, API_TAGS_QUERY + selectedTag);
    }
  };

  const handleCategoryClick = (categoryValue?: string) => {
    if (!categoryValue && selectedOrg.uuid) {
      getApiListAuthenticated(selectedOrg.uuid);
    } else if (categoryValue && selectedOrg.uuid) {
      // Double uri encode the value to preserve the whitespaces in the categoryValue
      const selectedCategory = encodeURIComponent(encodeURIComponent(categoryValue));
      getApiListAuthenticated(
        selectedOrg.uuid,
        API_CATEGORY_QUERY + selectedCategory
      );
    }
  };

  const handleSearchBarChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setApiSearchBarText(event.target.value);
  };

  const handleSearchBarButtonClick = () => {
    const searchQuery = apiSearchBarText ? API_DISPLAY_NAME_QUERY + apiSearchBarText : '';
    if (selectedOrg.uuid && apimTokenObtained) {
      trackApiLoadOnSearch(searchQuery, tagList?.list.map(tag => tag.value));
      getApiListAuthenticated(
        selectedOrg.uuid,
        searchQuery
      );
    } else if (!apimTokenObtained && publicOrgUuid) {
      getApiListUnAuthenticated(
          publicOrgUuid,
          searchQuery
      );
    }
  };

  const getTagColor = (tagValue: number) => {
    const tagColor = DEFAULT_API_TAGS_COLOR;
    const { maxValue, minValue } = tagCounts;
    if (maxValue == 1) {
      return tagColor.replace(RGB_ALPHA, DEFAULT_RGB_ALPHA);
    }
    // Mapping the range of tag counts between 30% and 100% ALPHA value
    const colorGradientPercentage =
      (7 * tagValue - 10 * minValue + 3 * maxValue) /
      (10 * (maxValue - minValue));
    return tagColor.replace(RGB_ALPHA, colorGradientPercentage.toString());
  };

  useEffect(() => {
    if (apiList) {
      setApiListState((prevState) => {
        return {
          ...prevState,
          apiList: apiList,
        };
      });
    }
  }, [apiList]);

  useEffect(() => {
    if (isValidData(thumbnailMap)) {
      setApiListState((prevState) => {
        return {
          ...prevState,
          thumbnailMap: thumbnailMap,
        };
      });
    }
  }, [thumbnailMap]);

  useEffect(() => {
    if (tagList) {
      setApiListState((prevState) => {
        return {
          ...prevState,
          tagList: tagList.list,
        };
      });
      setTagCounts({
        minValue: Math.min(...tagList.list.map((tag) => tag.count)),
        maxValue: Math.max(...tagList.list.map((tag) => tag.count)),
      });
    }
  }, [tagList]);

  useEffect(() => {
    if (categoryList) {
      setApiListState((prevState) => {
        return {
          ...prevState,
          categoryList: categoryList.list,
        };
      });
    }
  }, [categoryList]);

  useEffect(() => {
    if (signedIn && orgName && !hasUserAccessToCurrentPathOrg) {
      history.push("/forbidden");
    }
  }, [selectedOrg]);

  useEffect(() => {
    if (!apimTokenObtained) {
      // @TODO Temporarily disabling anonymous view
      // fetchApiUnauthenticated(apiId);
      sessionStoreOperation(
        StorageOperations.ADD,
        RETURN_TO_PATH,
        window.location.pathname
      );
      const queryParam = new URLSearchParams(location.search);
      const orgUuid = queryParam.get(ORG_QUERY_PARAM);
      if (orgUuid) {
        localStoreOperation(StorageOperations.ADD, SELECTED_ORG_UUID, orgUuid);
      }
      // Checking whether request is from choreo portal and setting fidp value
      if (queryParam.get('fidp')) {
        localStoreOperation(
          StorageOperations.ADD,
          FIDP,
          queryParam.get('fidp') ?? ''
        );
        history.push(RESOURCE_SIGN_IN);
      } else if (localStoreOperation(StorageOperations.GET, FIDP) && orgName == localStoreOperation(StorageOperations.GET, SELECTED_ORG_HANDLE_NAME)) {
        // Redirecting user to sigin if fidp value already exists in local store and orghandle in url and local store are equivalent
        history.push(RESOURCE_SIGN_IN);
      } else if (publicOrgUuid) {
        // None of the users need to be navigated to login page, if the fidp value is not exists then all the users
        // will be navigated to public API view (Listing public APIs)
        getApiListUnAuthenticated(publicOrgUuid);
      }
    } else if (
      apimTokenObtained &&
      selectedOrg.uuid &&
      hasUserAccessToCurrentPathOrg
    ) {
      getApiListAuthenticated(selectedOrg.uuid);
      fetchApiTagList(selectedOrg.uuid);
      fetchApiCategoryList(selectedOrg.uuid);
    }
  }, [selectedOrg]);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <main className={classes.content} id='apiListMainContent'>
          <div className={classes.appBarSpacer} />
          <Container maxWidth={'xl'}>
            <Grid container>
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                style={{ marginBottom: '68px' }}
              >
                <Typography
                  align='center'
                  color='textPrimary'
                  className={classes.apiListPageTitle}
                >
                  {theme.custom.apiListingPage.title.text ??
                    intl.formatMessage({
                      id: 'apiListingPage.banner.title',
                      defaultMessage: 'Find and Connect to APIs',
                    })}
                </Typography>
                <Typography
                  align='center'
                  paragraph
                  className={classes.apiListPageSubTitle}
                >
                  {theme.custom.apiListingPage.subTitle.text ??
                    intl.formatMessage({
                      id: 'apiListingPage.banner.subTitle',
                      defaultMessage:
                        'Explore our APIs and discover how you can use them to develop your application',
                    })}
                </Typography>
                <OutlinedInput
                  id='outlined-search-bar-api-listing'
                  type={'text'}
                  placeholder={intl.formatMessage({
                    id: 'apiListingPage.apiSearchBar.placeholder',
                    defaultMessage: 'Search APIs',
                  })}
                  value={apiSearchBarText}
                  onChange={handleSearchBarChange}
                  onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === KEY_ENTER) {
                      handleSearchBarButtonClick();
                    }
                  }}
                  endAdornment={
                    <InputAdornment position='end'>
                      <Button
                        variant='contained'
                        color='primary'
                        disableElevation
                        className={classes.apiSearchBarButton}
                        onClick={handleSearchBarButtonClick}
                      >
                        <FormattedMessage
                          id='apiListingPage.searchBar.button.searchLabel'
                          defaultMessage='Search'
                        />
                      </Button>
                    </InputAdornment>
                  }
                  startAdornment={
                    <InputAdornment position='end'>
                      <img
                        src='../../themes/default/images/search.svg'
                        alt={intl.formatMessage({
                          id: 'apiListingPage.searchBar.searchIcon.alt',
                          defaultMessage: 'Search Icon',
                        })}
                      />
                    </InputAdornment>
                  }
                  labelWidth={0}
                  className={classes.apiSearchBar}
                  classes={{
                    adornedStart: classes.apiSearchBarAdornedStart,
                    adornedEnd: classes.apiSearchBarAdornedEnd,
                    inputAdornedStart: classes.apiSearchBarInputAdornedStart,
                  }}
                />
              </Grid>
              {apiLoading ? (
                <Grid container style={{ justifyContent: 'center' }}>
                  {[...Array(3).keys()].map((key) => (
                    <Grid
                      item
                      key={key}
                      xs={12}
                      sm={6}
                      md={6}
                      lg={4}
                      style={{ maxWidth: '300px' }}
                    >
                      <ApiCard
                        apiId={''}
                        apiVersion={''}
                        apiTitle={''}
                        apiImageUrl={''}
                        apiRating={0}
                        apiSummary={''}
                        organizationHandle={orgName}
                        userReducer={userReducer}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : apiListState.apiList.list?.length > 0 ? (
                <Grid container>
                  <Grid
                    item
                    lg={2}
                    md={2}
                    sm={12}
                    xs={12}
                    className={classes.apiTagCategorySection}
                  >
                    {apiListState.categoryList.length > 0 && (
                      <Box>
                        <Typography className={classes.apiTagCategoryTitle}>
                          {theme.custom.apiListingPage.tagCategory
                            .categoryTitle ??
                            intl.formatMessage({
                              id: 'apiListingPage.leftMenu.apiCategories.title',
                              defaultMessage: 'API Categories',
                            })}
                        </Typography>
                        <Box className={classes.apiCategoriesBox}>
                          <div className={classes.apiCategories}>
                            <CssBaseline />
                            {apiListState.categoryList.map((category) => {
                              return (
                                <li key={category.id}>
                                  <Chip
                                    label={category.name}
                                    className={classes.categoryChip}
                                    onClick={() =>
                                      handleCategoryClick(category.name)
                                    }
                                    variant='outlined'
                                  />
                                </li>
                              );
                            })}
                          </div>
                        </Box>
                      </Box>
                    )}
                    <Box>
                      <Typography className={classes.apiTagCategoryTitle}>
                        {theme.custom.apiListingPage.tagCategory.tagsTitle ??
                          intl.formatMessage({
                            id: 'apiListingPage.leftMenu.apiTags.title',
                            defaultMessage: 'Tags',
                          })}
                      </Typography>
                      <Box className={classes.apiCategoriesBox}>
                        <div className={classes.apiTags}>
                          <CssBaseline />
                          <Chip
                            label={intl.formatMessage({
                              id: 'apiListingPage.leftMenu.apiTags.allApis',
                              defaultMessage: 'All',
                            })}
                            className={classes.tagChip}
                            onClick={() => handleTagClick()}
                            variant='outlined'
                          />
                          {apiListState.tagList.map((tag) => {
                            return (
                              <li key={tag.value}>
                                <Chip
                                  label={tag.value + ' ( ' + tag.count + ' )'}
                                  className={classes.tagChip}
                                  onClick={() => handleTagClick(tag.value)}
                                  variant='outlined'
                                  style={{
                                    backgroundColor: getTagColor(tag.count),
                                  }}
                                />
                              </li>
                            );
                          })}
                        </div>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid container item lg={10} md={10} sm={12} xs={12} >
                      <Grid container item spacing={0}>
                        {apiListState.apiList.list.map((api: Api) => (
                          <Grid
                            item
                            key={api.id}
                            xs={12}
                            sm={6}
                            md={6}
                            lg={2}
                            className={classes.apiListGrid}
                          >
                            <ApiCard
                              apiId={api.id}
                              apiVersion={api.version}
                              apiTitle={api.displayName}
                              apiImageUrl={apiListState.thumbnailMap[api.id]}
                              apiRating={parseFloat(api.avgRating)}
                              apiSummary={api.description}
                              organizationHandle={orgName}
                              userReducer={userReducer}
                              lifeCycleStatus={api.lifeCycleStatus}
                              testId={'apiCard-' + api.name}
                              isInternalAPI={getIsInternalAPI(api)}
                              apiType={getAPIType(api)}
                            />
                          </Grid>
                        ))}
                      </Grid>
                  </Grid>
                  <Grid
                    item
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginLeft: 'auto',
                      margin: '42px auto',
                    }}
                  >
                    <IconButton
                      onClick={getApiPreviousPage}
                      disabled={
                        apiListState.apiList?.pagination?.previous
                          ? false
                          : true
                      }
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography>
                      {apiListState.apiList?.pagination?.offset + 1}-
                      {apiListState.apiList?.pagination?.offset +
                        apiListState.apiList?.count}{' '}
                      of {apiListState.apiList?.pagination?.total}
                    </Typography>
                    <IconButton
                      onClick={getApiNextPage}
                      disabled={
                        apiListState.apiList?.pagination?.next ? false : true
                      }
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ) : (
                <Grid container style={{ justifyContent: 'center' }}>
                  <Grid item xl={3} lg={3} md={4} sm={6} xs={12}>
                    <img
                      src={
                        theme.custom.apiListingPage.noApisImage.src ??
                        '../themes/default/images/no-apis.svg'
                      }
                    />
                    <Typography
                      component='div'
                      className={classes.apiListPageSubTitle}
                    >
                      <FormattedMessage
                        id='apiListingPage.messages.noApisAvailable'
                        defaultMessage='No APIs available'
                      />
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Container>
          <div className={classes.appBarSpacer} />
        </main>
      <DevPortalFooter />
    </div>
  );
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    apiReducer: state.apiReducer,
    userReducer: state.userReducer,
    apiTagsReducer: state.apiTagsReducer,
    apiCategoriesReducer: state.apiCategoriesReducer,
    organizationMetadataReducer: state.organizationMetadataReducer,
  };
};

const mapDispatchToProps = {
  fetchApiListAuthenticated: fetchApiListAuthenticated,
  fetchApiListUnAuthenticated: fetchApiListUnAuthenticated,
  fetchApiListByMajorVersionAuthenticated: fetchApiListByMajorVersionAuthenticated,
  fetchApiListByMajorVersionUnAuthenticated: fetchApiListByMajorVersionUnAuthenticated,
  fetchApiTagList: fetchApiTagList,
  fetchApiCategoryList: fetchApiCategoryList,
};

export default connect(mapStateToProps, mapDispatchToProps)(ApiListPage);
